import React, { useEffect, useState } from 'react';
import '../../App.css';
import './visualisation.css';
import '../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import SessionStatus from './session-status';

// icons
import { Expand, Pin, PinOff, Shrink } from 'lucide-react';

// classes
import { TabBar, TabButton } from '../../tools/tab-component/tab-component';
import MiniButton from '../../tools/mini-button';
import Scrollbox from '../../tools/scrollbox';
import PleaseWait from '../../tools/please-wait';
import ComputeSession from '../compute-tiles/compute-session';

// types
import {
  CanfarServiceType,
  ComputeServiceType,
  JobType,
  Site,
  ToolType,
} from '../../utils/types';

// enum

// functions

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

// import state definition and functions.
import {
  setTab,
  closeTab,
  updateTab,
  setDisplayFullScreenMessage,
  setExpanded,
  setTabbarPinned,
  InteractiveSessionTab,
} from './interactive-sessions-slice';
import { findSessionForTab, isClosableTab } from './interactive-sessions-actions';
import { setSelectedTool } from '../../app-slice';

//  --------------------------------------------------------------------------
//
//  C O N S T A N T S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  H T M L   C U S T O M   C O M P O N E N T S
//
//  --------------------------------------------------------------------------

//  ------------------------------------------------------------
//
//  Displays the tool window for a given tool.
//
//  ------------------------------------------------------------

function ToolWindow(args: { tab: InteractiveSessionTab }) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  // keep a list of visible tools. these are running sessions, available services, and web resources.
  const sSessions: ComputeServiceType[] = useAppSelector(
    (state) => state.interactiveSessions.sessions,
  );

  // sessions-loading flags.
  const sSessionsLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[] = useAppSelector((state) => state.interactiveSessions.sessionsLoading);

  // global state.
  const sG_site: Site | undefined = useAppSelector((state) => state.global.site);
  const sG_selectedCanfarId: string | undefined = useAppSelector(
    (state) => state.global.selectedCanfarId,
  );

  // app slice.
  const sSelectedTool: ToolType = useAppSelector((state) => state.app.selectedTool);

  //  -------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  -------------------------------------------------

  // extract the list of active canfar sessions that are loading.
  const activeSessionsCanfarLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[] = sSessionsLoading.filter(
    (item) => item.resource_type === 'canfar' && item.compute_status === 'running',
  );

  // extract the list of available canfar sessions that are loading.
  const availableSessionsCanfarLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[] = sSessionsLoading.filter(
    (item) => item.resource_type === 'canfar' && item.compute_status === 'available',
  );

  // determine if the active SRCNet sessions are loading.
  const activeSessionsSRCNetLoading: boolean =
    sSessionsLoading.findIndex(
      (item) => item.resource_type === 'srcnet' && item.compute_status === 'running',
    ) > -1;

  // find the current session in the array, and check if it's running.
  const _session: ComputeServiceType | undefined = findSessionForTab(args.tab);
  const _isSessionRunning: boolean =
    _session?.compute_status === 'running' && _session?.overridden_status !== 'removing';

  //  -------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  -------------------------------------------------

  if (args.tab.toolComponent !== undefined) {
    //
    // a tool component has been specified, so this tool should be displayed in the tab.
    const Tool = args.tab.toolComponent;
    return <Tool running={true} />;
    //
  } else if (
    args.tab.compute_service_id !== undefined ||
    args.tab.webSessionType !== undefined
  ) {
    //
    // we can display both the session in an iframe, and also the info page at the same time. One of them will be hidden by display: none.
    // we cannot display the session unless it is running.
    return (
      <>
        <div
          style={{
            flex: '1 1',
            height: '100%',
            display:
              args.tab.infoDisplayed !== true && _isSessionRunning === true
                ? 'flex'
                : 'none',
          }}
        >
          <iframe src={args.tab.url ?? ''} className="iframe" />
        </div>
        <div
          style={{
            display:
              args.tab.infoDisplayed === true || _isSessionRunning !== true
                ? 'flex'
                : 'none',
            flex: '1 1',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {args.tab.compute_service_id !== undefined && (
            <SessionStatus computeServiceId={args.tab.compute_service_id} />
          )}
        </div>
      </>
    );
    //
  } else {
    //
    // we have no tool component or service specified, so this must be the Sessions tab.
    return (
      <Scrollbox direction="vertical" height="100%" flex="1 1" flexDirection="column">
        <div
          className="text-colour interactive-sessions-section-title"
          style={{
            flex: '0 0',
            margin: '20px 0px 30px 10px',
          }}
        >
          You have the following active session(s). Click on a tile to open the session:
        </div>
        <div
          className="interactive-sessions-tile-row"
          style={{
            display: 'flex',
            flexDirection: 'row',
            flex: '0 0 auto',
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            flexWrap: 'nowrap',
          }}
        >
          {(activeSessionsCanfarLoading.findIndex(
            (item) => item.id === sG_selectedCanfarId,
          ) > -1 ||
            activeSessionsSRCNetLoading) && (
            <div style={{ marginLeft: '10px', marginBottom: '10px' }}>
              <PleaseWait text="Loading....." visible={true} />
            </div>
          )}
          {sSessions
            .filter(
              (item) =>
                (item.resource_type === 'canfar' || item.resource_type === 'srcnet') &&
                (item.compute_status.toLowerCase() !== 'available' ||
                  item.overridden_status === 'starting') &&
                ['carta', 'jupyterhub', 'notebook', 'desktop', 'headless'].includes(
                  item.session_type.toLowerCase(),
                ) &&
                item.site.site_uuid === (sG_site?.site_uuid ?? ''),
            )
            .map((item, index) => (
              <div
                key={item.compute_service_id ?? index}
                className="interactive-sessions-tile-item"
                style={{ flex: '0 0 auto', marginRight: '50px' }}
              >
                <ComputeSession
                  session={item}
                  pageVisible={sSelectedTool === ToolType.INTERACTIVE_SESSIONS}
                />
              </div>
            ))}
        </div>
        <div
          className="text-colour interactive-sessions-section-title"
          style={{
            flex: '0 0',
            margin: '35px 0px 30px 10px',
          }}
        >
          You can launch an additional session by clicking on one of the tile(s) below:
        </div>
        <div
          className="interactive-sessions-tile-row"
          style={{
            display: 'flex',
            flexDirection: 'row',
            flex: '0 0 auto',
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            flexWrap: 'nowrap',
          }}
        >
          {availableSessionsCanfarLoading.findIndex(
            (item) => item.id === sG_selectedCanfarId,
          ) > -1 && (
            <div style={{ marginLeft: '10px', marginBottom: '10px' }}>
              <PleaseWait text="Loading....." visible={true} />
            </div>
          )}
          {sSessions
            .filter(
              (item) =>
                (item.resource_type === 'canfar' || item.resource_type === 'srcnet') &&
                item.compute_status === 'available' &&
                ['carta', 'jupyterhub', 'notebook', 'desktop', 'headless'].includes(
                  item.session_type,
                ) &&
                item.site.site_uuid === (sG_site?.site_uuid ?? ''),
            )
            .map((item, index) => (
              <div
                key={item.compute_service_id ?? index}
                className="interactive-sessions-tile-item"
                style={{ flex: '0 0 auto', marginRight: '50px' }}
              >
                <ComputeSession
                  session={item}
                  pageVisible={sSelectedTool === ToolType.INTERACTIVE_SESSIONS}
                />
              </div>
            ))}
        </div>
        <div
          className="text-colour interactive-sessions-section-title"
          style={{
            flex: '0 0',
            margin: '35px 0px 30px 10px',
          }}
        >
          You can launch a web resource by clicking on one of the tile(s) below:
        </div>
        <div
          className="interactive-sessions-tile-row"
          style={{
            display: 'flex',
            flexDirection: 'row',
            flex: '0 0 auto',
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            flexWrap: 'nowrap',
          }}
        >
          {sSessions
            .filter((item) => item.resource_type === 'www')
            .map((item, index) => (
              <div
                key={item.compute_service_id ?? index}
                className="interactive-sessions-tile-item"
                style={{ flex: '0 0 auto', marginRight: '50px' }}
              >
                <ComputeSession
                  session={item}
                  pageVisible={sSelectedTool === ToolType.INTERACTIVE_SESSIONS}
                />
              </div>
            ))}
        </div>
      </Scrollbox>
    );
    //
  }
  //
} // ToolWindow

//  --------------------------------------------------------------------------
//
//  C L A S S / M A I N   F U N C T I O N   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export default function InteractiveSessionsWindow() {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // currently selected tool.
  const sSelectedTool: ToolType = useAppSelector((state) => state.app.selectedTool);

  // tab information.
  const sTab: number = useAppSelector((state) => state.interactiveSessions.tab);
  const sTabs: InteractiveSessionTab[] = useAppSelector(
    (state) => state.interactiveSessions.tabs,
  );
  const _selectedTab: InteractiveSessionTab | undefined =
    sTab >= 0 && sTab < sTabs.length ? sTabs[sTab] : undefined;

  // get the sessions list, and the session for the currently-selected tab.
  const sSessions: ComputeServiceType[] = useAppSelector(
    (state) => state.interactiveSessions.sessions,
  );
  const _session: ComputeServiceType | undefined = findSessionForTab(_selectedTab);
  const sSessionsLoading: {
    resource_type: string;
    compute_status: string;
    id?: string;
  }[] = useAppSelector((state) => state.interactiveSessions.sessionsLoading);

  // global state.
  const sG_site: Site | undefined = useAppSelector((state) => state.global.site);

  // data state.
  const sD_canfarServices: CanfarServiceType[] = useAppSelector(
    (state) => state.data.canfarServices ?? [],
  );

  // staging jobs list.
  const sJobs: JobType[] = useAppSelector(
    (state) => state.fileBrowser.dataManagementJobs,
  );

  // get the canfar instance for this site.
  const _canfarService: CanfarServiceType | undefined =
    sG_site?.site_uuid !== undefined
      ? sD_canfarServices.find((service) => service.site.site_uuid === sG_site?.site_uuid)
      : undefined;

  // subscribe so tab titles re-render when sessions[] is refreshed.
  useAppSelector((state) => state.interactiveSessions.sessions);
  const sExpanded: boolean = useAppSelector(
    (state) => state.interactiveSessions.expanded,
  );
  const sTabbarPinned: boolean = useAppSelector(
    (state) => state.interactiveSessions.tabbarPinned,
  );
  const sDisplayFullScreenMessage: boolean = useAppSelector(
    (state) => state.interactiveSessions.displayFullScreenMessage,
  );

  //  --------------------------------------------------------------------------
  //
  //  C O N S T A N T S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // multi-language support
  const { i18n, t } = useTranslation();

  const [sTabbarExpanded, setTabbarExpanded] = useState<boolean>(false);
  const [sSetFocusTrigger, setSetFocusTrigger] = useState<number>(0);

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  set the message timeout when the session is made full screen.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    let timeout: NodeJS.Timeout;

    if (sExpanded === true) {
      dispatch(setDisplayFullScreenMessage(true));
      timeout = setTimeout(() => dispatch(setDisplayFullScreenMessage(false)), 3000);
    } else {
      dispatch(setDisplayFullScreenMessage(false));
    }

    return () => clearTimeout(timeout);
    //
  }, [sExpanded]);

  //  ------------------------------------------------------------
  //
  //  Set the focus to the pin button if the user expands
  //  the collapsed tabbar.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    if (sSetFocusTrigger > 0) {
      const target = document.getElementById('pin-unpin-interactive-tabbar');
      if (target instanceof HTMLElement) {
        target.focus();
      }
    }
    //
  }, [sSetFocusTrigger]);

  //  ------------------------------------------------------------
  //
  //  If the list of sessions has changed then we need to
  //  check if any open tabs are linked to sessions that no longer
  //  exist. If so, we must close the tab.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    // make a list of tabs with session ids that no longer exist in the array.
    let tabsToClose: number[] = [];
    for (let i = 0; i < sTabs.length; i++) {
      const session: ComputeServiceType | undefined = findSessionForTab(sTabs[i]);
      if (session === undefined && sTabs[i].compute_service_id !== undefined) {
        //
        // check if services for the current CANFAR instance are currently being refreshed. Services are temporarily removed while being refreshed,
        // but we don't want to close the tab under this circumstance.
        const refreshingServices: boolean =
          sSessionsLoading.find(
            (find) =>
              find.resource_type === 'canfar' &&
              find.compute_status === 'running' &&
              find.id === _canfarService?.id,
          ) !== undefined;

        // we want to close this tab if the service is not currently being refreshed.
        if (refreshingServices === false) {
          tabsToClose.push(i);
        }
        //
      }
    }

    // close these tabs.
    for (let i = tabsToClose.length - 1; i >= 0; i--) {
      dispatch(closeTab({ tab: tabsToClose[i] }));
    }

    // check if any tabs are associated with sessions that have recently moved to status running.
    sTabs.forEach((tab) => {
      //
      const session: ComputeServiceType | undefined = findSessionForTab(tab);
      if (
        tab.sessionRunning === false &&
        session?.compute_status === 'running' &&
        session?.overridden_status !== 'removing'
      ) {
        //
        let update: any = {
          id: tab.uniqueID,
          url: session?.service_url,
          sessionRunning: true,
        };
        if ([undefined, true].includes(tab.stagingComplete)) {
          //
          update = { ...update, ...{ infoDisplayed: false } };

          // if this session is not embedded, then we also need to launch it in
          // a separate browser tab.
          if (session.is_embedded !== true) {
            window.open(session.service_url, '_blank', 'noreferrer');
          }
          //
        }

        // show the session (i.e. switch from the info view to the session view), and set the session running.
        dispatch(updateTab(update));
        //
      }
      //
    });
    //
  }, [sSessions, sSessionsLoading]);

  //  ------------------------------------------------------------
  //
  //  re-check all tabs to see if there is an associated
  //  data-staging request, and whether or not that request is
  //  complete.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    sTabs.forEach((tab: InteractiveSessionTab) => {
      //
      // find the session.
      const session: ComputeServiceType | undefined = findSessionForTab(tab);
      if (session !== undefined) {
        //
        // get a list of data-staging requests associated with this session, and check if any are incomplete.
        let updatedStagingComplete: boolean = true;
        const jobs: JobType[] = sJobs.filter(
          (filter) => filter.skaha_session_id === session.compute_service_id,
        );
        jobs.forEach((job: JobType) => {
          if (
            ['preparing', 'unchecked', 'staging', 'retrying', 'unknown'].includes(
              job.status.toLowerCase(),
            )
          ) {
            updatedStagingComplete = false;
          }
        });

        // if the staging complete flag has changed then update the tab.
        if (tab.stagingComplete !== updatedStagingComplete && jobs.length > 0) {
          //
          let updatedTab: {
            id: number;
            stagingComplete?: boolean;
            infoDisplayed?: boolean;
          } = {
            id: tab.uniqueID,
            stagingComplete: updatedStagingComplete,
          };

          // update the tab to view the session.
          if (updatedStagingComplete === true && tab.sessionRunning === true) {
            updatedTab = { ...updatedTab, ...{ infoDisplayed: false } };
          }
          dispatch(updateTab(updatedTab));

          // if this session is not embedded, then we also need to launch it in
          // a separate browser tab.
          if (
            updatedStagingComplete === true &&
            tab.sessionRunning === true &&
            session.is_embedded !== true
          ) {
            window.open(session.service_url, '_blank', 'noreferrer');
          }

          //
        }

        // if there are no jobs attached to this session then clear the staging flag.
        if (tab.stagingComplete !== undefined && jobs.length === 0) {
          dispatch(
            updateTab({
              id: tab.uniqueID,
              stagingComplete: -1, // set undefined
            }),
          );
        }
        //
      }
      //
    });
    //
  }, [sTabs, sJobs]);

  //  -------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  -------------------------------------------------

  return (
    <div
      style={{
        display: sSelectedTool === ToolType.INTERACTIVE_SESSIONS ? 'flex' : 'none',
        width: '100%',
        height: '100%',
        position: sExpanded ? 'fixed' : 'static',
        left: '0px',
        top: '0px',
        flexDirection: 'column',
        zIndex: '20',
      }}
    >
      {
        //
        <div
          className="panel-main"
          style={{
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: '0px',
            paddingTop: sExpanded ? '0px' : '5px',
          }}
        >
          {/*
            ///  an unpinned header row will collapse unless the mouse is hovered.
            */}
          <button
            role="navigation"
            aria-label="View tab bar"
            className="panel-background text-colour"
            type="button"
            style={{
              display:
                sTabbarPinned === false && sTabbarExpanded === false ? 'flex' : 'none',
              flex: '0 0 auto',
              margin: '0px 5px 0px 5px',
              justifyContent: 'center',
              fontSize: '0.7em',
              padding: '0px 0px 2px 0px',
              lineHeight: '10px',
            }}
            onMouseEnter={() => setTabbarExpanded(true)}
            onClick={() => {
              setTabbarExpanded(true);
              setSetFocusTrigger(Date.now());
            }}
          >
            ▼
          </button>

          {/*
          ///  we have a header row containing the tabs for running tools
          */}
          <TabBar
            aria-label="Navigate running sessions"
            data-hide={sTabbarPinned === false && sTabbarExpanded === false ? 'T' : 'F'}
            data-full-screen={sExpanded ? 'T' : 'F'}
            onMouseLeave={() => setTabbarExpanded(false)}
            onBlur={(evt) => {
              if (
                evt.currentTarget.contains(evt.relatedTarget) !== true ||
                evt.relatedTarget === null
              )
                setTabbarExpanded(false);
            }}
          >
            <div style={{ flex: '0 0 8px' }} />
            <MiniButton
              id="pin-unpin-interactive-tabbar"
              className="text-colour"
              data-selected="F"
              onClick={() => dispatch(setTabbarPinned(sTabbarPinned === false))}
              aria-label={t((sTabbarPinned ? 'Unpin' : 'Pin') + ' tab bar')}
              title={t((sTabbarPinned ? 'Unpin' : 'Pin') + ' tab bar')}
              style={{
                margin: '0px 8px 0px 8px',
              }}
            >
              {sTabbarPinned ? (
                <Pin width={26} height={26} strokeWidth={1.5} />
              ) : (
                <PinOff width={26} height={26} strokeWidth={1.5} />
              )}
            </MiniButton>
            <div style={{ flex: '0 0 10px' }} />
            {
              // display one tab for each tool that is currently running.
              sTabs.map((item: InteractiveSessionTab, index) => {
                const tabSession: ComputeServiceType | undefined =
                  findSessionForTab(item);
                return (
                  <TabButton
                    key={
                      index.toString() +
                      (tabSession?.service_name ?? item.uniqueID.toString())
                    }
                    text={tabSession?.service_name ?? 'Sessions'}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      dispatch(setTab({ tab: index }));
                    }}
                    onClose={
                      isClosableTab(item)
                        ? (e: React.MouseEvent<HTMLButtonElement>) => {
                            dispatch(closeTab({ tab: index }));
                            e.stopPropagation();
                          }
                        : undefined
                    }
                    selected={sTab === index}
                    index={index}
                  />
                );
              })
            }
            <MiniButton
              id="expand-contract-interactive"
              className="text-colour"
              data-selected="F"
              onClick={() => dispatch(setExpanded(sExpanded === false))}
              aria-label={t((sExpanded ? 'Exit' : 'Enter') + ' full screen mode')}
              title={t((sExpanded ? 'Exit' : 'Enter') + ' full screen mode')}
              style={{
                margin: '0px 8px 0px 8px',
              }}
            >
              {sExpanded ? (
                <Shrink width={26} height={26} strokeWidth={1.5} />
              ) : (
                <Expand width={26} height={26} strokeWidth={1.5} />
              )}
            </MiniButton>
            <div style={{ flex: '0 0 8px' }} />
          </TabBar>

          {
            // display the tool windows, making them visible only if the
            // currently selected tab matches this tool.
            sTabs.map((item: InteractiveSessionTab, index) => (
              <div
                key={item.uniqueID}
                className="visualisation-section"
                data-visible={sTab === index ? 'T' : 'F'}
                data-full-screen={sExpanded ? 'T' : 'F'}
                style={{
                  position: 'relative',
                }}
              >
                <ToolWindow tab={item} />

                {/* display the full-screen message */}
                <div
                  className="panel-background text-colour interactive-sessions-fullscreen-message"
                  style={{
                    width: 'min(600px, 92vw)',
                    height: 'auto',
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    top: '15px',
                    fontSize: '1.3em',
                    padding: '20px',
                    display: sDisplayFullScreenMessage ? 'flex' : 'none',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  Press minimise button (top right) to exit full-screen mode
                </div>
              </div>
            ))
          }
        </div>
        //
      }
    </div>
  );
  //
} // InteractiveSessionsWindow
